// src/pages/admin/AdminProgress.tsx
// Route: /admin/progress

import { useState, useMemo, useEffect } from "react";
import {
  useAdminGetSchedulesQuery,
  useAdminCreateScheduleMutation,
  useAdminGetAttendanceQuery,
  useAdminMarkAttendanceBatchMutation,
  useAdminGetReportsQuery,
  useAdminCreateReportMutation,
  useAdminPublishReportMutation,
  useAdminUnpublishReportMutation,
  useAdminDeleteReportMutation,
} from "../../app/api/progressReportApi";
import { useAdminGetEnrolmentsQuery } from "../../app/api/enrolmentApi";
import type {
  ProgressReport,
  AttendanceStatus,
  TechnicalRating,
  CommunicationRating,
  EffortRating,
  Quarter,
  CreateScheduleBody,
} from "../../app/api/progressReportApi";
import type { StudentTrack } from "../../app/api/authApi";
import {
  PageHeader,
  Skeleton,
  EmptyState,
  TableContainer,
  Th,
  Td,
  Pagination,
  SearchInput,
  Select,
  Button,
  RatingChip,
  AttendanceChip,
  Avatar,
  formatDate,
  trackConfig,
} from "./AdminShared";

import { CalendarDays, Check, ClipboardList, Mic, NotebookPen, TriangleAlert, X } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import Ico from "../../components/Ico";
/* ─── HELPERS ────────────────────────────────────────────────────── */

const QUARTERS: Quarter[] = ["Q1", "Q2", "Q3"];
const TRACKS = ["foundation_builders", "explorers", "innovators"] as const;

function currentAcademicYear() {
  const y = new Date().getFullYear();
  const m = new Date().getMonth();
  return m >= 8 ? `${y}-${y + 1}` : `${y - 1}-${y}`;
}

const ATTENDANCE_STATUSES: {
  value: AttendanceStatus;
  label: string;
  color: string;
  bg: string;
}[] = [
  { value: "present", label: "Present", color: "#2a7a56", bg: "#edfaf4" },
  { value: "late", label: "Late", color: "#7a4a10", bg: "#fff8ee" },
  { value: "absent", label: "Absent", color: "#9b2a2a", bg: "#fff3f3" },
  { value: "excused", label: "Excused", color: "#8189a0", bg: "#f1f3f8" },
];

/* ═══════════════════════════════════════════════════════════════════
   TAB 1 — QUARTER SCHEDULES
═══════════════════════════════════════════════════════════════════ */

function ScheduleForm({ onDone }: { onDone: () => void }) {
  const [createSchedule, { isLoading }] = useAdminCreateScheduleMutation();
  const [academicYear, setAcademicYear] = useState(currentAcademicYear());
  const [quarter, setQuarter] = useState<Quarter>("Q1");
  const [track, setTrack] = useState<(typeof TRACKS)[number]>(
    "foundation_builders",
  );
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [showcaseDate, setShowcaseDate] = useState("");
  const [sessions, setSessions] = useState<
    Array<{ weekNumber: number; sessionDate: string; topic: string }>
  >([]);
  const [error, setError] = useState("");

  const generateSessions = () => {
    if (!startDate) {
      setError("Set a start date first");
      return;
    }
    const start = new Date(startDate);
    const weeks = track === "innovators" ? 18 : 12;
    setSessions(
      Array.from({ length: weeks }, (_, i) => {
        const d = new Date(start);
        d.setDate(d.getDate() + i * 7);
        return {
          weekNumber: i + 1,
          sessionDate: d.toISOString().split("T")[0],
          topic: "",
        };
      }),
    );
  };

  const handleSubmit = async () => {
    if (!academicYear || !quarter || !track || !startDate || !endDate) {
      setError("All fields are required");
      return;
    }
    setError("");
    try {
      const body: CreateScheduleBody = {
        academicYear,
        quarter,
        track,
        startDate,
        endDate,
        showcaseDate: showcaseDate || undefined,
        sessions: sessions.map((s) => ({
          weekNumber: s.weekNumber,
          sessionDate: s.sessionDate,
          topic: s.topic || undefined,
        })),
      };
      await createSchedule(body).unwrap();
      onDone();
    } catch (err: any) {
      setError(err?.data?.error ?? "Failed to create schedule");
    }
  };

  return (
    <div className="rounded-[22px] border border-[#e5e8ef] bg-white p-6">
      <h3 className="mb-5 text-base font-bold text-[#16213e]">
        Create quarter schedule
      </h3>
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label className="text-[12.5px] font-bold uppercase tracking-wider text-[#8189a0]">
            Academic year
          </label>
          <input
            value={academicYear}
            onChange={(e) => setAcademicYear(e.target.value)}
            placeholder="2025-2026"
            className="mt-1.5 w-full rounded-xl border border-[#dde2ec] px-3 py-2.5 text-sm outline-none focus:border-[#1b2d5e]"
          />
        </div>
        <div>
          <label className="text-[12.5px] font-bold uppercase tracking-wider text-[#8189a0]">
            Quarter
          </label>
          <select
            value={quarter}
            onChange={(e) => setQuarter(e.target.value as Quarter)}
            className="mt-1.5 w-full rounded-xl border border-[#dde2ec] px-3 py-2.5 text-sm outline-none focus:border-[#1b2d5e]"
          >
            {QUARTERS.map((q) => (
              <option key={q} value={q}>
                {q}
              </option>
            ))}
          </select>
        </div>
        <div className="sm:col-span-2">
          <label className="text-[12.5px] font-bold uppercase tracking-wider text-[#8189a0]">
            Track
          </label>
          <select
            value={track}
            onChange={(e) =>
              setTrack(e.target.value as (typeof TRACKS)[number])
            }
            className="mt-1.5 w-full rounded-xl border border-[#dde2ec] px-3 py-2.5 text-sm outline-none focus:border-[#1b2d5e]"
          >
            <option value="foundation_builders">
              Foundation Builders (6–9)
            </option>
            <option value="explorers">Explorers (9–12)</option>
            <option value="innovators">Innovators (13–18)</option>
          </select>
        </div>
        <div>
          <label className="text-[12.5px] font-bold uppercase tracking-wider text-[#8189a0]">
            Start date
          </label>
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="mt-1.5 w-full rounded-xl border border-[#dde2ec] px-3 py-2.5 text-sm outline-none focus:border-[#1b2d5e]"
          />
        </div>
        <div>
          <label className="text-[12.5px] font-bold uppercase tracking-wider text-[#8189a0]">
            End date
          </label>
          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className="mt-1.5 w-full rounded-xl border border-[#dde2ec] px-3 py-2.5 text-sm outline-none focus:border-[#1b2d5e]"
          />
        </div>
        <div>
          <label className="text-[12.5px] font-bold uppercase tracking-wider text-[#8189a0]">
            Showcase date (optional)
          </label>
          <input
            type="date"
            value={showcaseDate}
            onChange={(e) => setShowcaseDate(e.target.value)}
            className="mt-1.5 w-full rounded-xl border border-[#dde2ec] px-3 py-2.5 text-sm outline-none focus:border-[#1b2d5e]"
          />
        </div>
        <div className="flex items-end">
          <Button
            variant="secondary"
            className="w-full"
            onClick={generateSessions}
          >
            Auto-generate {track === "innovators" ? 18 : 12} sessions
          </Button>
        </div>
      </div>

      {sessions.length > 0 && (
        <div className="mt-5">
          <p className="mb-3 text-[12.5px] font-bold uppercase tracking-wider text-[#8189a0]">
            Weekly sessions ({sessions.length})
          </p>
          <div className="max-h-64 overflow-y-auto space-y-2 rounded-[16px] border border-[#ebeff7] bg-[#f9fafc] p-3">
            {sessions.map((s, i) => (
              <div key={i} className="flex items-center gap-2">
                <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-[#ebeff7] text-[11.5px] font-bold text-[#1b2d5e]">
                  {s.weekNumber}
                </span>
                <input
                  type="date"
                  value={s.sessionDate}
                  onChange={(e) =>
                    setSessions((prev) =>
                      prev.map((x, j) =>
                        j === i ? { ...x, sessionDate: e.target.value } : x,
                      ),
                    )
                  }
                  className="rounded-lg border border-[#dde2ec] px-2 py-1 text-xs outline-none focus:border-[#1b2d5e]"
                />
                <input
                  type="text"
                  value={s.topic}
                  placeholder="Topic (optional)"
                  onChange={(e) =>
                    setSessions((prev) =>
                      prev.map((x, j) =>
                        j === i ? { ...x, topic: e.target.value } : x,
                      ),
                    )
                  }
                  className="flex-1 rounded-lg border border-[#dde2ec] px-2 py-1 text-xs outline-none focus:border-[#1b2d5e]"
                />
              </div>
            ))}
          </div>
        </div>
      )}

      {error && <p className="mt-3 text-xs text-[#e57373]">{error}</p>}
      <div className="mt-5 flex gap-3">
        <Button variant="secondary" className="flex-1" onClick={onDone}>
          Cancel
        </Button>
        <Button
          variant="primary"
          className="flex-1"
          onClick={handleSubmit}
          disabled={isLoading}
        >
          {isLoading ? "Creating…" : "Create schedule"}
        </Button>
      </div>
    </div>
  );
}

function SchedulesTab() {
  const [showForm, setShowForm] = useState(false);
  const [filterTrack, setFilterTrack] = useState("");
  const [filterQuarter, setFilterQuarter] = useState("");

  const { data, isLoading } = useAdminGetSchedulesQuery({
    ...(filterTrack ? { track: filterTrack as any } : {}),
    ...(filterQuarter ? { quarter: filterQuarter as any } : {}),
  });
  const schedules = data?.data ?? [];

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="text-base font-bold text-[#16213e]">
          Quarter schedules
        </h2>
        <div className="ml-auto flex flex-wrap items-center gap-2">
          <Select value={filterTrack} onChange={setFilterTrack}>
            <option value="">All tracks</option>
            <option value="foundation_builders">Foundation</option>
            <option value="explorers">Explorers</option>
            <option value="innovators">Innovators</option>
          </Select>
          <Select value={filterQuarter} onChange={setFilterQuarter}>
            <option value="">All quarters</option>
            <option value="Q1">Q1</option>
            <option value="Q2">Q2</option>
            <option value="Q3">Q3</option>
          </Select>
          <Button
            variant="primary"
            size="sm"
            onClick={() => setShowForm(!showForm)}
          >
            {showForm ? "Cancel" : "+ New schedule"}
          </Button>
        </div>
      </div>

      {showForm && <ScheduleForm onDone={() => setShowForm(false)} />}

      {isLoading ? (
        <div className="space-y-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-24 rounded-[18px]" />
          ))}
        </div>
      ) : schedules.length === 0 ? (
        <EmptyState
          icon={CalendarDays}
          title="No schedules yet"
          subtitle="Create a quarter schedule to set session dates for each track."
          action={
            <Button variant="primary" onClick={() => setShowForm(true)}>
              Create first schedule
            </Button>
          }
        />
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {schedules.map((s) => {
            const tc = trackConfig(s.track);
            return (
              <div
                key={s._id}
                className="rounded-[20px] border border-[#e5e8ef] bg-white p-5 shadow-[0_2px_8px_rgba(27,45,94,0.04)]"
              >
                <div className="flex items-start justify-between gap-2 mb-3">
                  <div>
                    <span
                      className="inline-flex items-center rounded-full px-2.5 py-0.5 text-[12.5px] font-semibold"
                      style={{
                        background: tc.bg,
                        color: tc.text,
                        border: `1px solid ${tc.border}`,
                      }}
                    >
                      {tc.label}
                    </span>
                    <p className="mt-1.5 text-base font-bold text-[#16213e]">
                      {s.quarter} · {s.academicYear}
                    </p>
                  </div>
                  <span
                    className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11.5px] font-bold ${s.isActive ? "bg-[#edfaf4] text-[#2a7a56]" : "bg-[#f1f3f8] text-[#8189a0]"}`}
                  >
                    {s.isActive ? "Active" : "Inactive"}
                  </span>
                </div>
                <div className="space-y-1 text-xs text-[#5a6278]">
                  <p>
                    <CalendarDays className="inline-block h-[1em] w-[1em] align-[-0.125em]" aria-hidden />{" "}Starts:{" "}
                    <span className="font-semibold text-[#16213e]">
                      {formatDate(s.startDate)}
                    </span>
                  </p>
                  <p>
                    <CalendarDays className="inline-block h-[1em] w-[1em] align-[-0.125em]" aria-hidden />{" "}Ends:{" "}
                    <span className="font-semibold text-[#16213e]">
                      {formatDate(s.endDate)}
                    </span>
                  </p>
                  {s.showcaseDate && (
                    <p>
                      <Mic className="inline-block h-[1em] w-[1em] align-[-0.125em]" aria-hidden />{" "}Showcase:{" "}
                      <span className="font-semibold text-[#16213e]">
                        {formatDate(s.showcaseDate)}
                      </span>
                    </p>
                  )}
                  <p>
                    <CalendarDays className="inline-block h-[1em] w-[1em] align-[-0.125em]" aria-hidden />{" "}Sessions:{" "}
                    <span className="font-semibold text-[#16213e]">
                      {s.sessions.length}
                    </span>
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════
   TAB 2 — ATTENDANCE
═══════════════════════════════════════════════════════════════════ */

function AttendanceTab() {
  const [markBatch, { isLoading: saving }] =
    useAdminMarkAttendanceBatchMutation();

  // Step 1 — select schedule
  const [selectedScheduleId, setSelectedScheduleId] = useState<string>("");
  const [selectedWeekNumber, setSelectedWeekNumber] = useState<number>(0);

  // Fetch all schedules for the picker
  const { data: schedulesData, isLoading: schedulesLoading } =
    useAdminGetSchedulesQuery({});
  const schedules = schedulesData?.data ?? [];

  const selectedSchedule =
    schedules.find((s) => s._id === selectedScheduleId) ?? null;
  const selectedSession =
    selectedSchedule?.sessions.find(
      (s) => s.weekNumber === selectedWeekNumber,
    ) ?? null;

  // When schedule changes, default to the first session
  useEffect(() => {
    if (selectedSchedule && selectedSchedule.sessions.length > 0) {
      setSelectedWeekNumber(selectedSchedule.sessions[0].weekNumber);
    }
  }, [selectedScheduleId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Step 2 — load enrolled children for this schedule's track
  const { data: enrolmentsData, isLoading: enrolmentsLoading } =
    useAdminGetEnrolmentsQuery(
      {
        track: selectedSchedule?.track as StudentTrack,
        limit: 100,
        status: "confirmed",
      },
      { skip: !selectedSchedule },
    );
  const enrolments = enrolmentsData?.data ?? [];

  // Per-child attendance state: { [enrolmentId]: { status, note } }
  const [attendance, setAttendance] = useState<
    Record<string, { status: AttendanceStatus; note: string }>
  >({});
  const [markError, setMarkError] = useState("");
  const [markSuccess, setMarkSuccess] = useState("");

  // Reset attendance when schedule or week changes
  useEffect(() => {
    setAttendance({});
    setMarkError("");
    setMarkSuccess("");
  }, [selectedScheduleId, selectedWeekNumber]);

  // Initialise all children as "present" when enrolments load
  useEffect(() => {
    if (enrolments.length > 0) {
      const init: Record<string, { status: AttendanceStatus; note: string }> =
        {};
      enrolments.forEach((e) => {
        init[e.id] = { status: "present", note: "" };
      });
      setAttendance(init);
    }
  }, [enrolments.length, selectedWeekNumber]); // eslint-disable-line react-hooks/exhaustive-deps

  // Mark all
  const markAll = (status: AttendanceStatus) => {
    setAttendance((prev) => {
      const next = { ...prev };
      Object.keys(next).forEach((id) => {
        next[id] = { ...next[id], status };
      });
      return next;
    });
  };

  const handleSave = async () => {
    if (!selectedSchedule || !selectedSession) {
      setMarkError("Select a schedule and week first");
      return;
    }
    if (enrolments.length === 0) {
      setMarkError("No enrolled students found for this track");
      return;
    }

    setMarkError("");
    setMarkSuccess("");
    try {
      await markBatch({
        academicYear: selectedSchedule.academicYear,
        quarter: selectedSchedule.quarter,
        weekNumber: selectedWeekNumber,
        sessionDate: selectedSession.sessionDate,
        records: enrolments.map((e) => ({
          enrolmentId: e.id,
          status: attendance[e.id]?.status ?? "present",
          note: attendance[e.id]?.note || undefined,
        })),
      }).unwrap();
      setMarkSuccess(
        `Attendance saved for ${enrolments.length} student${enrolments.length > 1 ? "s" : ""}`,
      );
    } catch (err: any) {
      setMarkError(err?.data?.error ?? "Failed to save attendance");
    }
  };

  // Records view filters
  const [viewQuarter, setViewQuarter] = useState("");
  const [viewWeek, setViewWeek] = useState("");
  const [viewStatus, setViewStatus] = useState("");

  const { data: recordsData, isLoading: recordsLoading } =
    useAdminGetAttendanceQuery({
      ...(viewQuarter ? { quarter: viewQuarter as any } : {}),
      ...(viewWeek ? { weekNumber: Number(viewWeek) } : {}),
      ...(viewStatus ? { status: viewStatus as any } : {}),
    });
  const records = recordsData?.data ?? [];

  return (
    <div className="space-y-6">
      {/* ── STEP 1: Select schedule ─────────────────────────────── */}
      <div className="rounded-[22px] border border-[#e5e8ef] bg-white p-6">
        <h3 className="mb-4 text-base font-bold text-[#16213e]">
          Mark attendance
        </h3>

        {/* Schedule + Week pickers */}
        <div className="grid gap-3 sm:grid-cols-2 mb-5">
          <div>
            <label className="text-[12.5px] font-bold uppercase tracking-wider text-[#8189a0]">
              Quarter schedule
            </label>
            {schedulesLoading ? (
              <Skeleton className="mt-1.5 h-10 rounded-xl" />
            ) : (
              <select
                value={selectedScheduleId}
                onChange={(e) => setSelectedScheduleId(e.target.value)}
                className="mt-1.5 w-full rounded-xl border border-[#dde2ec] px-3 py-2.5 text-sm outline-none focus:border-[#1b2d5e]"
              >
                <option value="">— Select a schedule —</option>
                {schedules.map((s) => {
                  const tc = trackConfig(s.track);
                  return (
                    <option key={s._id} value={s._id}>
                      {tc.label} · {s.quarter} · {s.academicYear}
                    </option>
                  );
                })}
              </select>
            )}
          </div>

          <div>
            <label className="text-[12.5px] font-bold uppercase tracking-wider text-[#8189a0]">
              Session / Week
            </label>
            {!selectedSchedule ? (
              <div className="mt-1.5 h-10 rounded-xl border border-[#dde2ec] bg-[#f9fafc]" />
            ) : (
              <select
                value={selectedWeekNumber}
                onChange={(e) => setSelectedWeekNumber(Number(e.target.value))}
                className="mt-1.5 w-full rounded-xl border border-[#dde2ec] px-3 py-2.5 text-sm outline-none focus:border-[#1b2d5e]"
              >
                {selectedSchedule.sessions.map((s) => (
                  <option key={s.weekNumber} value={s.weekNumber}>
                    Week {s.weekNumber} —{" "}
                    {s.sessionLabel ?? formatDate(s.sessionDate)}
                    {s.topic ? ` · ${s.topic}` : ""}
                  </option>
                ))}
              </select>
            )}
          </div>
        </div>

        {/* Schedule summary banner */}
        {selectedSchedule && selectedSession && (
          <div className="mb-5 flex flex-wrap items-center gap-3 rounded-[16px] border border-[#cdd4e2] bg-[#f1f3f8] px-4 py-3">
            <CalendarDays className="h-4 w-4 flex-shrink-0" aria-hidden />
            <div className="flex-1">
              <p className="text-xs font-bold text-[#16213e]">
                {trackConfig(selectedSchedule.track).label} ·{" "}
                {selectedSchedule.quarter} · Week {selectedWeekNumber}
              </p>
              <p className="text-[12.5px] text-[#5f6780]">
                Session date: {formatDate(selectedSession.sessionDate)}
                {selectedSession.topic && ` · ${selectedSession.topic}`}
              </p>
            </div>
          </div>
        )}

        {/* ── STEP 2: Children list ────────────────────────────────── */}
        {selectedSchedule &&
          (enrolmentsLoading ? (
            <div className="space-y-2">
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} className="h-14 rounded-xl" />
              ))}
            </div>
          ) : enrolments.length === 0 ? (
            <div className="rounded-[16px] border border-dashed border-[#cdd4e2] bg-[#f9fafc] py-8 text-center">
              <p className="text-sm text-[#8189a0]">
                No confirmed enrolled students found for this track.
              </p>
            </div>
          ) : (
            <>
              {/* Bulk actions */}
              <div className="mb-3 flex items-center gap-2">
                <p className="text-xs font-semibold text-[#3b4560]">
                  {enrolments.length} student{enrolments.length > 1 ? "s" : ""}
                </p>
                <div className="ml-auto flex items-center gap-1.5">
                  <span className="text-[11.5px] font-bold uppercase tracking-wider text-[#8189a0] mr-1">
                    Mark all:
                  </span>
                  {ATTENDANCE_STATUSES.map((s) => (
                    <button
                      key={s.value}
                      onClick={() => markAll(s.value)}
                      className="rounded-full px-2.5 py-1 text-[11.5px] font-bold transition hover:opacity-80"
                      style={{ background: s.bg, color: s.color }}
                    >
                      {s.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Student rows */}
              <div className="space-y-2">
                {enrolments.map((e) => {
                  const child = e.child;
                  const att = attendance[e.id] ?? {
                    status: "present" as AttendanceStatus,
                    note: "",
                  };
                  // eslint-disable-next-line @typescript-eslint/no-unused-vars
                  const statusCfg = ATTENDANCE_STATUSES.find(
                    (s) => s.value === att.status,
                  )!;
                  return (
                    <div
                      key={e.id}
                      className="flex items-center gap-3 rounded-[16px] border border-[#e5e8ef] bg-white px-4 py-3 transition hover:border-[#cdd4e2]"
                    >
                      {/* Avatar + name */}
                      <div className="flex items-center gap-2.5 w-44 flex-shrink-0">
                        {child ? (
                          <Avatar
                            firstName={child.firstName}
                            lastName={child.lastName}
                            size="sm"
                          />
                        ) : (
                          <span className="flex h-7 w-7 items-center justify-center rounded-[8px] bg-[#ebeff7] text-[11.5px] font-bold text-[#1b2d5e]">
                            ?
                          </span>
                        )}
                        <div className="min-w-0">
                          <p className="text-xs font-bold text-[#16213e] truncate">
                            {child
                              ? `${child.firstName} ${child.lastName}`
                              : "Unknown"}
                          </p>
                          <p className="text-[11.5px] text-[#8189a0]">
                            Age {child?.age}
                          </p>
                        </div>
                      </div>

                      {/* Status toggle buttons */}
                      <div className="flex flex-1 gap-1.5">
                        {ATTENDANCE_STATUSES.map((s) => (
                          <button
                            key={s.value}
                            onClick={() =>
                              setAttendance((prev) => ({
                                ...prev,
                                [e.id]: { ...prev[e.id], status: s.value },
                              }))
                            }
                            className="flex-1 rounded-xl py-1.5 text-[11.5px] font-bold transition"
                            style={
                              att.status === s.value
                                ? {
                                    background: s.bg,
                                    color: s.color,
                                    border: `1.5px solid ${s.color}40`,
                                  }
                                : {
                                    background: "#f1f3f8",
                                    color: "#8189a0",
                                    border: "1.5px solid #dde2ec",
                                  }
                            }
                          >
                            {s.label}
                          </button>
                        ))}
                      </div>

                      {/* Note input */}
                      <input
                        value={att.note ?? ""}
                        onChange={(e2) =>
                          setAttendance((prev) => ({
                            ...prev,
                            [e.id]: { ...prev[e.id], note: e2.target.value },
                          }))
                        }
                        placeholder="Note…"
                        className="w-28 flex-shrink-0 rounded-xl border border-[#dde2ec] px-2.5 py-1.5 text-[11.5px] outline-none focus:border-[#1b2d5e]"
                      />
                    </div>
                  );
                })}
              </div>

              {/* Save button */}
              <div className="mt-4 flex items-center gap-3">
                {markError && (
                  <p className="flex-1 text-xs text-[#e57373]">{markError}</p>
                )}
                {markSuccess && (
                  <p className="flex-1 text-xs font-semibold text-[#6cc2a0]">
                    {markSuccess}
                  </p>
                )}
                <Button
                  variant="primary"
                  className="ml-auto"
                  onClick={handleSave}
                  disabled={saving}
                >
                  {saving
                    ? "Saving…"
                    : `Save attendance for ${enrolments.length} student${enrolments.length > 1 ? "s" : ""}`}
                </Button>
              </div>
            </>
          ))}

        {!selectedSchedule && (
          <div className="rounded-[16px] border border-dashed border-[#cdd4e2] bg-[#f9fafc] py-10 text-center">
            <p className="text-sm text-[#8189a0]">
              Select a quarter schedule above to see enrolled students.
            </p>
          </div>
        )}
      </div>

      {/* ── Attendance records view ──────────────────────────────── */}
      <div>
        <div className="mb-4 flex flex-wrap items-center gap-3">
          <h3 className="text-base font-bold text-[#16213e]">
            Attendance records
          </h3>
          <div className="ml-auto flex flex-wrap items-center gap-2">
            <Select value={viewQuarter} onChange={setViewQuarter}>
              <option value="">All quarters</option>
              <option value="Q1">Q1</option>
              <option value="Q2">Q2</option>
              <option value="Q3">Q3</option>
            </Select>
            <Select value={viewStatus} onChange={setViewStatus}>
              <option value="">All statuses</option>
              <option value="present">Present</option>
              <option value="late">Late</option>
              <option value="absent">Absent</option>
              <option value="excused">Excused</option>
            </Select>
            <input
              type="number"
              min="1"
              max="18"
              value={viewWeek}
              onChange={(e) => setViewWeek(e.target.value)}
              placeholder="Week #"
              className="w-20 rounded-xl border border-[#dde2ec] px-3 py-2 text-xs outline-none focus:border-[#1b2d5e]"
            />
          </div>
        </div>

        {recordsLoading ? (
          <div className="space-y-2">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="h-12 rounded-xl" />
            ))}
          </div>
        ) : records.length === 0 ? (
          <EmptyState
            icon={ClipboardList}
            title="No attendance records"
            subtitle="Records appear after you save attendance above."
          />
        ) : (
          <TableContainer>
            <table className="w-full text-left text-sm">
              <thead className="border-b border-[#ebeff7]">
                <tr>
                  <Th>Student</Th>
                  <Th>Week</Th>
                  <Th>Session date</Th>
                  <Th>Status</Th>
                  <Th>Note</Th>
                  <Th>Marked by</Th>
                </tr>
              </thead>
              <tbody>
                {records.map((r) => (
                  <tr key={r.id} className="border-b border-[#f7f8fb]">
                    <Td>
                      <p className="font-semibold text-[#16213e]">
                        {r.childName ?? "Unknown"}
                      </p>
                      <p className="font-mono text-[11.5px] text-[#a6adbf]">
                        {String(r.enrolment).slice(-8)}
                      </p>
                    </Td>
                    <Td>
                      <span className="font-bold text-[#3b4560]">
                        Week {r.weekNumber}
                      </span>
                    </Td>
                    <Td>
                      <span className="text-xs">
                        {r.sessionDateLabel ?? formatDate(r.sessionDate)}
                      </span>
                    </Td>
                    <Td>
                      <AttendanceChip status={r.status} />
                    </Td>
                    <Td>
                      <span className="max-w-[160px] truncate text-xs text-[#5a6278]">
                        {r.note ?? "—"}
                      </span>
                    </Td>
                    <Td>
                      <span className="text-xs text-[#8189a0]">
                        {r.markedBy ?? "—"}
                      </span>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableContainer>
        )}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════
   TAB 3 — PROGRESS REPORTS
═══════════════════════════════════════════════════════════════════ */

function ReportForm({ onDone }: { onDone: () => void }) {
  const [createReport, { isLoading }] = useAdminCreateReportMutation();

  // ── Step 1: Schedule selection (mirrors AttendanceTab) ──────────
  const { data: schedulesData, isLoading: schedulesLoading } =
    useAdminGetSchedulesQuery({});
  const schedules = schedulesData?.data ?? [];

  const [selectedScheduleId, setSelectedScheduleId] = useState<string>("");
  const [selectedWeekNumber, setSelectedWeekNumber] = useState<number>(0);
  const [selectedEnrolmentId, setSelectedEnrolmentId] = useState<string>("");

  const selectedSchedule =
    schedules.find((s) => s._id === selectedScheduleId) ?? null;
  const selectedSession =
    selectedSchedule?.sessions.find(
      (s) => s.weekNumber === selectedWeekNumber,
    ) ?? null;

  // Default to first session when schedule changes
  useEffect(() => {
    if (selectedSchedule && selectedSchedule.sessions.length > 0) {
      setSelectedWeekNumber(selectedSchedule.sessions[0].weekNumber);
    }
    setSelectedEnrolmentId("");
  }, [selectedScheduleId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setSelectedEnrolmentId("");
  }, [selectedWeekNumber]);

  // ── Step 2: Enrolled children for this track ────────────────────
  const { data: enrolmentsData, isLoading: enrolmentsLoading } =
    useAdminGetEnrolmentsQuery(
      {
        track: selectedSchedule?.track as StudentTrack,
        limit: 100,
        status: "confirmed",
      },
      { skip: !selectedSchedule },
    );
  const enrolments = enrolmentsData?.data ?? [];
  const selectedEnrolment =
    enrolments.find((e) => e.id === selectedEnrolmentId) ?? null;

  // ── Report fields ───────────────────────────────────────────────
  const [reportDate, setReportDate] = useState(
    new Date().toISOString().split("T")[0],
  );
  const [sessionTopic, setSessionTopic] = useState("");
  const [whatWasBuilt, setWhatWasBuilt] = useState("");
  const [currentProject, setCurrentProject] = useState("");
  const [skillsDemonstrated, setSkillsDemonstrated] = useState("");
  const [areasToStrengthen, setAreasToStrengthen] = useState("");
  const [technicalRating, setTechnicalRating] =
    useState<TechnicalRating>("developing");
  const [communicationRating, setCommunicationRating] =
    useState<CommunicationRating>("developing");
  const [effortRating, setEffortRating] = useState<EffortRating>("moderate");
  const [progressPercent, setProgressPercent] = useState("0");
  const [tutorNote, setTutorNote] = useState("");
  const [parentAction, setParentAction] = useState("");
  const [requiresAttn, setRequiresAttn] = useState(false);
  const [attended, setAttended] = useState(true);
  const [publish, setPublish] = useState(false);
  const [error, setError] = useState("");

  // Pre-fill topic from schedule session when week changes
  useEffect(() => {
    if (selectedSession?.topic) setSessionTopic(selectedSession.topic);
    if (selectedSession?.sessionDate)
      setReportDate(selectedSession.sessionDate);
  }, [selectedSession?.weekNumber]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmit = async () => {
    if (!selectedEnrolmentId) {
      setError("Select a student first");
      return;
    }
    if (!selectedWeekNumber) {
      setError("Select a week first");
      return;
    }
    setError("");
    try {
      await createReport({
        enrolmentId: selectedEnrolmentId,
        weekNumber: selectedWeekNumber,
        reportDate,
        sessionTopic: sessionTopic || undefined,
        whatWasBuilt: whatWasBuilt || undefined,
        currentProject: currentProject || undefined,
        skillsDemonstrated: skillsDemonstrated
          ? skillsDemonstrated
              .split(",")
              .map((s) => s.trim())
              .filter(Boolean)
          : [],
        areasToStrengthen: areasToStrengthen
          ? areasToStrengthen
              .split(",")
              .map((s) => s.trim())
              .filter(Boolean)
          : [],
        technicalRating,
        communicationRating,
        effortRating,
        progressPercent: Number(progressPercent),
        tutorNote: tutorNote || undefined,
        parentAction: parentAction || undefined,
        requiresParentAttention: requiresAttn,
        attended,
        isPublished: publish,
      }).unwrap();
      onDone();
    } catch (err: any) {
      setError(err?.data?.error ?? "Failed to create report");
    }
  };

  const inputCls =
    "w-full rounded-xl border border-[#dde2ec] px-3 py-2.5 text-sm outline-none focus:border-[#1b2d5e]";
  const ratingBtn = (selected: boolean) =>
    `flex-1 rounded-xl border py-2 text-center text-xs font-semibold cursor-pointer transition ${selected ? "border-[#1b2d5e] bg-[#ebeff7] text-[#4a44b0]" : "border-[#dde2ec] text-[#8189a0] hover:border-[#c9cfe0]"}`;
  const Toggle = ({
    on,
    onClick,
    label,
    color = "#1b2d5e",
  }: {
    on: boolean;
    onClick: () => void;
    label: string;
    color?: string;
  }) => (
    <label className="flex items-center gap-2 cursor-pointer">
      <div
        onClick={onClick}
        className="h-5 w-9 rounded-full transition-colors"
        style={{ background: on ? color : "#dde2ec" }}
      >
        <div
          className={`mt-0.5 h-4 w-4 rounded-full bg-white shadow transition-transform ${on ? "translate-x-4" : "translate-x-0.5"}`}
        />
      </div>
      <span className="text-xs font-semibold text-[#3b4560]">{label}</span>
    </label>
  );

  return (
    <div className="rounded-[22px] border border-[#e5e8ef] bg-white p-6 space-y-5">
      <h3 className="text-base font-bold text-[#16213e]">
        Write progress report
      </h3>

      {/* ── Step 1: Schedule + Week ─────────────────────────────── */}
      <div className="rounded-[16px] border border-[#cdd4e2] bg-[#f1f3f8] p-4 space-y-3">
        <p className="text-[12.5px] font-bold uppercase tracking-wider text-[#8189a0]">
          1 · Select schedule & session
        </p>
        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <label className="text-[12.5px] font-semibold text-[#5a6278]">
              Quarter schedule
            </label>
            {schedulesLoading ? (
              <div className="mt-1.5 h-10 animate-pulse rounded-xl bg-[#dde2ec]" />
            ) : (
              <select
                value={selectedScheduleId}
                onChange={(e) => setSelectedScheduleId(e.target.value)}
                className="mt-1.5 w-full rounded-xl border border-[#dde2ec] bg-white px-3 py-2.5 text-sm outline-none focus:border-[#1b2d5e]"
              >
                <option value="">— Select a schedule —</option>
                {schedules.map((s) => {
                  const tc = trackConfig(s.track);
                  return (
                    <option key={s._id} value={s._id}>
                      {tc.label} · {s.quarter} · {s.academicYear}
                    </option>
                  );
                })}
              </select>
            )}
          </div>
          <div>
            <label className="text-[12.5px] font-semibold text-[#5a6278]">
              Session / Week
            </label>
            {!selectedSchedule ? (
              <div className="mt-1.5 h-10 rounded-xl border border-[#dde2ec] bg-[#f9fafc]" />
            ) : (
              <select
                value={selectedWeekNumber}
                onChange={(e) => setSelectedWeekNumber(Number(e.target.value))}
                className="mt-1.5 w-full rounded-xl border border-[#dde2ec] bg-white px-3 py-2.5 text-sm outline-none focus:border-[#1b2d5e]"
              >
                {selectedSchedule.sessions.map((s) => (
                  <option key={s.weekNumber} value={s.weekNumber}>
                    Week {s.weekNumber} —{" "}
                    {s.sessionLabel ?? formatDate(s.sessionDate)}
                    {s.topic ? ` · ${s.topic}` : ""}
                  </option>
                ))}
              </select>
            )}
          </div>
        </div>
      </div>

      {/* ── Step 2: Select student ──────────────────────────────── */}
      {selectedSchedule && (
        <div className="rounded-[16px] border border-[#cdd4e2] bg-[#f1f3f8] p-4 space-y-3">
          <p className="text-[12.5px] font-bold uppercase tracking-wider text-[#8189a0]">
            2 · Select student
          </p>
          {enrolmentsLoading ? (
            <div className="space-y-2">
              {Array.from({ length: 3 }).map((_, i) => (
                <div
                  key={i}
                  className="h-12 animate-pulse rounded-xl bg-[#dde2ec]"
                />
              ))}
            </div>
          ) : enrolments.length === 0 ? (
            <p className="text-sm text-[#8189a0]">
              No confirmed enrolled students for this track.
            </p>
          ) : (
            <div className="grid gap-2 sm:grid-cols-2">
              {enrolments.map((e) => {
                const child = e.child;
                const isSelected = selectedEnrolmentId === e.id;
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                const tc = trackConfig(e.track);
                return (
                  <button
                    key={e.id}
                    type="button"
                    onClick={() => setSelectedEnrolmentId(e.id)}
                    className={[
                      "flex items-center gap-3 rounded-[14px] border-2 px-3 py-2.5 text-left transition-all",
                      isSelected
                        ? "border-[#1b2d5e] bg-[#ebeff7] shadow-[0_0_0_3px_rgba(27,45,94,0.12)]"
                        : "border-[#dde2ec] bg-white hover:border-[#c9cfe0]",
                    ].join(" ")}
                  >
                    <span
                      className={[
                        "flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-[6px] border-2 transition-all",
                        isSelected
                          ? "border-[#1b2d5e] bg-[#1b2d5e]"
                          : "border-[#c9cfe0] bg-white",
                      ].join(" ")}
                    >
                      {isSelected && (
                        <svg
                          className="h-3 w-3 text-white"
                          viewBox="0 0 12 12"
                          fill="none"
                        >
                          <path
                            d="M2 6l3 3 5-5"
                            stroke="currentColor"
                            strokeWidth="1.8"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      )}
                    </span>
                    {child ? (
                      <Avatar
                        firstName={child.firstName}
                        lastName={child.lastName}
                        size="sm"
                      />
                    ) : (
                      <span className="flex h-7 w-7 items-center justify-center rounded-[8px] bg-[#ebeff7] text-[11.5px] font-bold text-[#1b2d5e]">
                        ?
                      </span>
                    )}
                    <div className="min-w-0 flex-1">
                      <p
                        className={`text-xs font-bold truncate ${isSelected ? "text-[#4a44b0]" : "text-[#16213e]"}`}
                      >
                        {child
                          ? `${child.firstName} ${child.lastName}`
                          : "Unknown"}
                      </p>
                      <p className="text-[11.5px] text-[#8189a0]">
                        Age {child?.age}
                      </p>
                    </div>
                  </button>
                );
              })}
            </div>
          )}

          {/* Selected student confirmation banner */}
          {selectedEnrolment && selectedEnrolment.child && (
            <div className="flex items-center gap-3 rounded-[12px] border border-[#b6e8d0] bg-[#edfaf4] px-3 py-2.5">
              <Check className="h-4 w-4 flex-shrink-0" aria-hidden />
              <p className="text-xs font-semibold text-[#2a7a56]">
                Writing report for {(selectedEnrolment.child as any).firstName}{" "}
                {(selectedEnrolment.child as any).lastName}
                {" · "}
                {selectedSchedule &&
                  `${selectedSchedule.quarter} Week ${selectedWeekNumber}`}
              </p>
            </div>
          )}
        </div>
      )}

      {/* ── Step 3: Report content (only shown once student is selected) ── */}
      {selectedEnrolmentId && (
        <>
          {/* Session info — pre-filled from schedule */}
          <div className="rounded-[16px] border border-[#cdd4e2] bg-[#f1f3f8] p-4 space-y-3">
            <p className="text-[12.5px] font-bold uppercase tracking-wider text-[#8189a0]">
              3 · Session details
            </p>
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <label className="text-[12.5px] font-semibold text-[#5a6278]">
                  Report date
                </label>
                <input
                  type="date"
                  value={reportDate}
                  onChange={(e) => setReportDate(e.target.value)}
                  className={inputCls + " mt-1.5"}
                />
              </div>
              <div>
                <label className="text-[12.5px] font-semibold text-[#5a6278]">
                  Session topic
                </label>
                <input
                  value={sessionTopic}
                  onChange={(e) => setSessionTopic(e.target.value)}
                  placeholder="e.g. Loops and sequences"
                  className={inputCls + " mt-1.5"}
                />
              </div>
              <div>
                <label className="text-[12.5px] font-semibold text-[#5a6278]">
                  Current project
                </label>
                <input
                  value={currentProject}
                  onChange={(e) => setCurrentProject(e.target.value)}
                  placeholder="e.g. Scratch interactive story"
                  className={inputCls + " mt-1.5"}
                />
              </div>
              <div>
                <label className="text-[12.5px] font-semibold text-[#5a6278]">
                  Quarter progress %
                </label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={progressPercent}
                  onChange={(e) => setProgressPercent(e.target.value)}
                  className={inputCls + " mt-1.5"}
                />
              </div>
            </div>
            <div>
              <label className="text-[12.5px] font-semibold text-[#5a6278]">
                What was built
              </label>
              <textarea
                value={whatWasBuilt}
                onChange={(e) => setWhatWasBuilt(e.target.value)}
                rows={2}
                placeholder="Describe what the student built this session…"
                className={inputCls + " mt-1.5 resize-none"}
              />
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <label className="text-[12.5px] font-semibold text-[#5a6278]">
                  Skills demonstrated (comma-separated)
                </label>
                <input
                  value={skillsDemonstrated}
                  onChange={(e) => setSkillsDemonstrated(e.target.value)}
                  placeholder="e.g. Sequencing, Loop logic"
                  className={inputCls + " mt-1.5"}
                />
              </div>
              <div>
                <label className="text-[12.5px] font-semibold text-[#5a6278]">
                  Areas to strengthen (comma-separated)
                </label>
                <input
                  value={areasToStrengthen}
                  onChange={(e) => setAreasToStrengthen(e.target.value)}
                  placeholder="e.g. Debugging, Explaining work"
                  className={inputCls + " mt-1.5"}
                />
              </div>
            </div>
          </div>

          {/* Ratings */}
          <div className="rounded-[16px] border border-[#ebeff7] bg-[#f9fafc] p-4 space-y-4">
            <p className="text-[12.5px] font-bold uppercase tracking-wider text-[#8189a0]">
              Ratings
            </p>
            {(
              [
                {
                  label: "Technical skill",
                  ratings: ["emerging", "developing", "strong", "excellent"],
                  value: technicalRating,
                  set: setTechnicalRating,
                },
                {
                  label: "Communication",
                  ratings: ["emerging", "developing", "strong", "excellent"],
                  value: communicationRating,
                  set: setCommunicationRating,
                },
                {
                  label: "Effort & engagement",
                  ratings: ["low", "moderate", "high"],
                  value: effortRating,
                  set: setEffortRating,
                },
              ] as const
            ).map((group) => (
              <div key={group.label}>
                <p className="mb-2 text-xs font-semibold text-[#5a6278]">
                  {group.label}
                </p>
                <div className="flex gap-2">
                  {group.ratings.map((r) => (
                    <button
                      key={r}
                      onClick={() => (group.set as any)(r)}
                      className={ratingBtn(group.value === r)}
                    >
                      {r.charAt(0).toUpperCase() + r.slice(1)}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Tutor note + parent action */}
          <div>
            <label className="text-[12.5px] font-bold uppercase tracking-wider text-[#8189a0]">
              Note from tutor (visible to parent)
            </label>
            <textarea
              value={tutorNote}
              onChange={(e) => setTutorNote(e.target.value)}
              rows={3}
              placeholder="Write a personal note to the parent about their child's progress…"
              className={inputCls + " mt-1.5 resize-none"}
            />
          </div>
          <div>
            <label className="text-[12.5px] font-bold uppercase tracking-wider text-[#8189a0]">
              Parent action (what to do at home)
            </label>
            <input
              value={parentAction}
              onChange={(e) => setParentAction(e.target.value)}
              placeholder="e.g. Ask your child to show you their Scratch project and explain it"
              className={inputCls}
            />
          </div>

          {/* Toggles */}
          <div className="flex flex-wrap gap-6">
            <Toggle
              on={attended}
              onClick={() => setAttended(!attended)}
              label="Student attended"
            />
            <Toggle
              on={requiresAttn}
              onClick={() => setRequiresAttn(!requiresAttn)}
              label="Requires parent attention"
              color="#f0a050"
            />
            <Toggle
              on={publish}
              onClick={() => setPublish(!publish)}
              label={`Publish immediately${publish ? " (parent will be notified)" : ""}`}
              color="#6cc2a0"
            />
          </div>
        </>
      )}

      {error && <p className="text-xs text-[#e57373]">{error}</p>}
      <div className="flex gap-3">
        <Button variant="secondary" className="flex-1" onClick={onDone}>
          Cancel
        </Button>
        <Button
          variant="primary"
          className="flex-1"
          onClick={handleSubmit}
          disabled={isLoading || !selectedEnrolmentId || !selectedWeekNumber}
        >
          {isLoading
            ? "Saving…"
            : !selectedEnrolmentId
              ? "Select a student first"
              : publish
                ? "Save & publish"
                : "Save as draft"}
        </Button>
      </div>
    </div>
  );
}

function ReportDetailDrawer({
  report,
  onClose,
}: {
  report: ProgressReport;
  onClose: () => void;
}) {
  const [publish, { isLoading: publishing }] = useAdminPublishReportMutation();
  const [unpublish, { isLoading: unpublishing }] =
    useAdminUnpublishReportMutation();
  const [deleteRpt, { isLoading: deleting }] = useAdminDeleteReportMutation();
  const [error, setError] = useState("");

  return (
    <>
      <div
        className="fixed inset-0 z-40 bg-[#16213e]/40 backdrop-blur-sm"
        onClick={onClose}
      />
      <div
        className="fixed inset-y-0 right-0 z-50 flex w-full flex-col bg-white shadow-[-8px_0_40px_rgba(27,45,94,0.12)] sm:max-w-[480px]"
        role="dialog"
        aria-modal
      >
        <div className="flex flex-shrink-0 items-center justify-between border-b border-[#ebeff7] px-6 py-5">
          <div>
            <h2 className="text-lg font-bold text-[#16213e]">
              Week {report.weekNumber} · {report.childName ?? "Unknown"}
            </h2>
            <p className="mt-0.5 text-xs text-[#8189a0]">
              {report.quarter} · {report.academicYear}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <span
              className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[12.5px] font-bold ${report.isPublished ? "bg-[#edfaf4] text-[#2a7a56]" : "bg-[#f1f3f8] text-[#8189a0]"}`}
            >
              {report.isPublished ? "Published" : "Draft"}
            </span>
            <button
              onClick={onClose}
              className="flex h-8 w-8 items-center justify-center rounded-xl text-[#8189a0] hover:bg-[#f1f3f8]"
            >
              <svg className="h-4 w-4" viewBox="0 0 16 16" fill="none">
                <path
                  d="M4 4l8 8M12 4l-8 8"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                />
              </svg>
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
          <div className="flex flex-wrap gap-2">
            <span
              className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[12.5px] font-semibold ${report.attended ? "bg-[#edfaf4] text-[#2a7a56]" : "bg-[#fff3f3] text-[#9b2a2a]"}`}
            >
              {report.attended ? <><Check className="inline-block h-[1em] w-[1em] align-[-0.125em]" aria-hidden /> Present</> : <><X className="inline-block h-[1em] w-[1em] align-[-0.125em]" aria-hidden /> Absent</>}
            </span>
            {report.requiresParentAttention && (
              <span className="inline-flex items-center rounded-full bg-[#fff8ee] border border-[#f5d9a0] px-2.5 py-0.5 text-[12.5px] font-semibold text-[#7a4a10]">
                <TriangleAlert className="inline-block h-[1em] w-[1em] align-[-0.125em]" aria-hidden />{" "}Parent attention needed
              </span>
            )}
          </div>

          {(report.sessionTopic || report.currentProject) && (
            <div className="rounded-[16px] border border-[#dde2ec] bg-[#f9fafc] overflow-hidden">
              {report.sessionTopic && (
                <div className="px-4 py-3 border-b border-[#ebeff7]">
                  <p className="text-[11.5px] font-bold text-[#8189a0] uppercase tracking-wider">
                    Topic
                  </p>
                  <p className="mt-0.5 text-sm font-semibold text-[#16213e]">
                    {report.sessionTopic}
                  </p>
                </div>
              )}
              {report.currentProject && (
                <div className="px-4 py-3">
                  <p className="text-[11.5px] font-bold text-[#8189a0] uppercase tracking-wider">
                    Project
                  </p>
                  <p className="mt-0.5 text-sm font-semibold text-[#16213e]">
                    {report.currentProject}
                  </p>
                </div>
              )}
            </div>
          )}

          <div className="grid grid-cols-3 gap-2">
            {[
              { label: "Technical", value: report.technicalRating },
              { label: "Communication", value: report.communicationRating },
              { label: "Effort", value: report.effortRating },
            ].map((r) => (
              <div
                key={r.label}
                className="rounded-[14px] border border-[#ebeff7] bg-[#f9fafc] px-3 py-3 text-center"
              >
                <p className="text-[11px] font-bold uppercase tracking-wider text-[#8189a0]">
                  {r.label}
                </p>
                <p className="mt-1 text-xs font-bold capitalize text-[#16213e]">
                  {r.value}
                </p>
              </div>
            ))}
          </div>

          <div>
            <div className="mb-1 flex justify-between text-xs">
              <span className="font-semibold text-[#5a6278]">
                Quarter progress
              </span>
              <span className="font-bold text-[#1b2d5e]">
                {report.progressPercent}%
              </span>
            </div>
            <div className="h-2 overflow-hidden rounded-full bg-[#ebeff7]">
              <div
                className="h-full rounded-full bg-[#1b2d5e]"
                style={{ width: `${report.progressPercent}%` }}
              />
            </div>
          </div>

          {report.skillsDemonstrated.length > 0 && (
            <div className="rounded-[16px] border border-[#b6e8d0] bg-[#edfaf4] p-4">
              <p className="mb-2 text-[11.5px] font-bold uppercase tracking-wider text-[#2a7a56]">
                Skills demonstrated
              </p>
              <div className="flex flex-wrap gap-1.5">
                {report.skillsDemonstrated.map((s, i) => (
                  <span
                    key={i}
                    className="rounded-full bg-white border border-[#b6e8d0] px-2.5 py-0.5 text-[12.5px] font-semibold text-[#2a7a56]"
                  >
                    {s}
                  </span>
                ))}
              </div>
            </div>
          )}

          {report.areasToStrengthen.length > 0 && (
            <div className="rounded-[16px] border border-[#cdd4e2] bg-[#ebeff7] p-4">
              <p className="mb-2 text-[11.5px] font-bold uppercase tracking-wider text-[#4a44b0]">
                Areas to strengthen
              </p>
              <div className="flex flex-wrap gap-1.5">
                {report.areasToStrengthen.map((s, i) => (
                  <span
                    key={i}
                    className="rounded-full bg-white border border-[#cdd4e2] px-2.5 py-0.5 text-[12.5px] font-semibold text-[#4a44b0]"
                  >
                    {s}
                  </span>
                ))}
              </div>
            </div>
          )}

          {report.tutorNote && (
            <div className="rounded-[18px] border-2 border-[#1b2d5e] p-5">
              <p className="mb-2 text-[11.5px] font-bold uppercase tracking-wider text-[#1b2d5e]">
                Tutor note
              </p>
              <p className="text-sm italic leading-7 text-[#3b4560]">
                "{report.tutorNote}"
              </p>
            </div>
          )}

          {report.parentAction && (
            <div className="rounded-[16px] bg-[#16213e] px-4 py-4 text-white">
              <p className="text-[11.5px] font-bold uppercase tracking-wider text-white/50 mb-1">
                Parent action
              </p>
              <p className="text-sm leading-6 text-white/90">
                {report.parentAction}
              </p>
            </div>
          )}

          <div className="flex items-center gap-2 rounded-[14px] border border-[#ebeff7] bg-[#f9fafc] px-4 py-3">
            <span
              className={`h-2 w-2 rounded-full ${report.readByParent ? "bg-[#6cc2a0]" : "bg-[#f0a050]"}`}
            />
            <p className="text-xs text-[#5a6278]">
              {report.readByParent
                ? `Read by parent on ${formatDate(report.readAt)}`
                : "Not yet read by parent"}
            </p>
          </div>

          {error && <p className="text-xs text-[#e57373]">{error}</p>}
        </div>

        <div className="flex-shrink-0 border-t border-[#ebeff7] px-6 py-5 space-y-2.5">
          {!report.isPublished && (
            <Button
              variant="primary"
              className="w-full"
              onClick={async () => {
                try {
                  await publish(report.id).unwrap();
                  onClose();
                } catch (e: any) {
                  setError(e?.data?.error ?? "Failed");
                }
              }}
              disabled={publishing}
            >
              {publishing
                ? "Publishing…"
                : <><Check className="inline-block h-[1em] w-[1em] align-[-0.125em]" aria-hidden /> Publish and email the parent</>}
            </Button>
          )}
          {report.isPublished && (
            <Button
              variant="secondary"
              className="w-full"
              onClick={async () => {
                try {
                  await unpublish(report.id).unwrap();
                  onClose();
                } catch (e: any) {
                  setError(e?.data?.error ?? "Failed");
                }
              }}
              disabled={unpublishing}
            >
              {unpublishing ? "Unpublishing…" : "Revert to draft"}
            </Button>
          )}
          {!report.isPublished && (
            <Button
              variant="danger"
              className="w-full"
              onClick={async () => {
                if (!window.confirm("Delete this draft?")) return;
                try {
                  await deleteRpt(report.id).unwrap();
                  onClose();
                } catch (e: any) {
                  setError(e?.data?.error ?? "Failed");
                }
              }}
              disabled={deleting}
            >
              {deleting ? "Deleting…" : "Delete draft"}
            </Button>
          )}
        </div>
      </div>
    </>
  );
}

function ReportsTab() {
  const [showForm, setShowForm] = useState(false);
  const [selected, setSelected] = useState<ProgressReport | null>(null);
  const [page, setPage] = useState(1);
  const [filterPub, setFilterPub] = useState("");
  const [filterTrack, setFilterTrack] = useState("");
  const [filterQuarter, setFilterQuarter] = useState("");
  const [search, setSearch] = useState("");

  const { data, isLoading } = useAdminGetReportsQuery({
    page,
    limit: 25,
    ...(filterPub ? { isPublished: filterPub === "true" } : {}),
    ...(filterTrack ? { track: filterTrack as any } : {}),
    ...(filterQuarter ? { quarter: filterQuarter as any } : {}),
  });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const reports = data?.data ?? [];

  const filtered = useMemo(() => {
    if (!search.trim()) return reports;
    const q = search.toLowerCase();
    return reports.filter(
      (r) =>
        (r.childName ?? "").toLowerCase().includes(q) ||
        (r.sessionTopic ?? "").toLowerCase().includes(q),
    );
  }, [reports, search]);

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="text-base font-bold text-[#16213e]">Progress reports</h2>
        <div className="ml-auto flex flex-wrap items-center gap-2">
          <SearchInput
            value={search}
            onChange={(v) => {
              setSearch(v);
              setPage(1);
            }}
            placeholder="Search by student or topic…"
          />
          <Select
            value={filterPub}
            onChange={(v) => {
              setFilterPub(v);
              setPage(1);
            }}
          >
            <option value="">All reports</option>
            <option value="true">Published</option>
            <option value="false">Drafts</option>
          </Select>
          <Select
            value={filterQuarter}
            onChange={(v) => {
              setFilterQuarter(v);
              setPage(1);
            }}
          >
            <option value="">All quarters</option>
            <option value="Q1">Q1</option>
            <option value="Q2">Q2</option>
            <option value="Q3">Q3</option>
          </Select>
          <Select
            value={filterTrack}
            onChange={(v) => {
              setFilterTrack(v);
              setPage(1);
            }}
          >
            <option value="">All tracks</option>
            <option value="foundation_builders">Foundation</option>
            <option value="explorers">Explorers</option>
            <option value="innovators">Innovators</option>
          </Select>
          <Button
            variant="primary"
            size="sm"
            onClick={() => setShowForm(!showForm)}
          >
            {showForm ? "Cancel" : "+ Write report"}
          </Button>
        </div>
      </div>

      {showForm && <ReportForm onDone={() => setShowForm(false)} />}

      {isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-14 rounded-xl" />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <EmptyState
          icon={NotebookPen}
          title="No reports found"
          action={
            <Button variant="primary" onClick={() => setShowForm(true)}>
              Write first report
            </Button>
          }
        />
      ) : (
        <TableContainer>
          <table className="w-full text-left text-sm">
            <thead className="border-b border-[#ebeff7]">
              <tr>
                <Th>Student</Th>
                <Th>Week</Th>
                <Th>Topic</Th>
                <Th>Technical</Th>
                <Th>Attendance</Th>
                <Th>Status</Th>
                <Th>Read</Th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => (
                <tr
                  key={r.id}
                  onClick={() => setSelected(r)}
                  className="cursor-pointer border-b border-[#f7f8fb] transition hover:bg-[#f9fafc]"
                >
                  <Td>
                    <p className="font-semibold text-[#16213e]">
                      {r.childName ?? "Unknown"}
                    </p>
                    <p className="text-[12.5px] text-[#8189a0]">
                      {r.quarter} · {r.academicYear}
                    </p>
                  </Td>
                  <Td>
                    <span className="font-bold text-[#3b4560]">
                      Week {r.weekNumber}
                    </span>
                  </Td>
                  <Td>
                    <span className="max-w-[140px] truncate text-xs text-[#5a6278]">
                      {r.sessionTopic ?? "—"}
                    </span>
                  </Td>
                  <Td>
                    <RatingChip rating={r.technicalRating} />
                  </Td>
                  <Td>
                    <span
                      className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11.5px] font-semibold ${r.attended ? "bg-[#edfaf4] text-[#2a7a56]" : "bg-[#fff3f3] text-[#9b2a2a]"}`}
                    >
                      {r.attended ? "Present" : "Absent"}
                    </span>
                  </Td>
                  <Td>
                    <span
                      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[12.5px] font-bold ${r.isPublished ? "bg-[#edfaf4] text-[#2a7a56]" : "bg-[#f1f3f8] text-[#8189a0]"}`}
                    >
                      {r.isPublished ? "Published" : "Draft"}
                    </span>
                  </Td>
                  <Td>
                    <span
                      className={`text-[12.5px] font-semibold ${r.readByParent ? "text-[#6cc2a0]" : "text-[#f0a050]"}`}
                    >
                      {r.readByParent ? <><Check className="inline-block h-[1em] w-[1em] align-[-0.125em]" aria-hidden /> Read</> : "Unread"}
                    </span>
                  </Td>
                  <Td>
                    <svg
                      className="h-4 w-4 text-[#c9cfe0]"
                      viewBox="0 0 16 16"
                      fill="none"
                    >
                      <path
                        d="M6 4l4 4-4 4"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
          <Pagination
            page={data?.page ?? 1}
            pages={data?.pages ?? 1}
            total={data?.total ?? 0}
            count={filtered.length}
            onPage={(p) => {
              setPage(p);
              window.scrollTo({ top: 0 });
            }}
          />
        </TableContainer>
      )}

      {selected && (
        <ReportDetailDrawer
          report={selected}
          onClose={() => setSelected(null)}
        />
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════
   ROOT PAGE
═══════════════════════════════════════════════════════════════════ */

type TabKey = "schedules" | "attendance" | "reports";

const TABS: { key: TabKey; label: string; icon: LucideIcon }[] = [
  { key: "schedules", label: "Quarter schedules", icon: CalendarDays },
  { key: "attendance", label: "Attendance", icon: Check },
  { key: "reports", label: "Progress reports", icon: NotebookPen },
];

export default function AdminProgress() {
  const [activeTab, setActiveTab] = useState<TabKey>("schedules");
  return (
    <>
      <PageHeader
        title="Progress"
        subtitle="Set quarter timelines, mark attendance, and write weekly progress reports for students."
      />
      <div className="mb-6 flex gap-1 overflow-x-auto rounded-2xl border border-[#e5e8ef] bg-white p-1.5">
        {TABS.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={[
              "flex flex-1 items-center justify-center gap-2 whitespace-nowrap rounded-xl px-4 py-2.5 text-xs font-semibold transition-all",
              activeTab === tab.key
                ? "bg-[#1b2d5e] text-white shadow-[0_4px_12px_rgba(27,45,94,0.30)]"
                : "text-[#8189a0] hover:bg-[#f1f3f8] hover:text-[#16213e]",
            ].join(" ")}
          >
            <span><Ico icon={tab.icon} /></span>
            <span>{tab.label}</span>
          </button>
        ))}
      </div>
      {activeTab === "schedules" && <SchedulesTab />}
      {activeTab === "attendance" && <AttendanceTab />}
      {activeTab === "reports" && <ReportsTab />}
    </>
  );
}
